import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import GoogleMapReact from "google-map-react";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";

import { InfoCard } from "./InfoCard";
import { COLORS } from "../../../../theme/colors";
import { Icon } from "../../../../components/base";
import { NO_DATA_TEXT } from "../../../../constants";
import { Toggle } from "../../../../components/toggle";
import { IconTitle } from "../../../../components/parts";
import { DEFAULT_CENTER } from "../../../../components/map";
import { GLANCE_PARAMS, GLANCE_RATES } from "../../constants";
import { LocationIcon } from "../../../../components/map/LocationIcon";
import { ActionTimeGroup } from "../../../../components/actionTimeTimeGroup";
import {
  currencyFormatter,
  nCO2Formatter,
  getData,
  getConditionalText,
} from "../../../../services";
import InAGlanceBuildingIcon from "../../../../assets/images/icons/in-a-glance-building.svg";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
  },
  title: {
    fontWeight: "bold",
  },
  timeRangeText: {
    textTransform: "uppercase",
    marginRight: theme.spacing(2),
    fontWeight: "bold",
  },
  rows: {
    minHeight: 304,
  },
  separated: {
    marginLeft: theme.spacing(0.6),
    marginRight: theme.spacing(0.6),
    fontWeight: "bold",
  },
  imageRoot: {
    height: "100%",
    width: "100%",
    objectFit: "cover",
  },
  locationBox: {
    height: 260,
    borderRadius: 4,
    overflow: "hidden",
  },
}));

const getRate = (rate) => {
  if (!rate) return null;
  return rate === "upwards" ? GLANCE_RATES.good : GLANCE_RATES.bad;
};

const defaultRevenueData = {
  rental_revenues: 0,
  rental_revenues_total: 0,
};

const defaultVacancyData = {
  physical_vacancy: 0,
  economic_vacancy: 0,
};

const defaultCarbonData = {
  carbon_reduction: 0,
  carbon_reduction_total: 0,
};

const defaultTenantsData = {
  percent_connected_tenants: 0,
  connected_tenants_count: 0,
  total_connected_tenants: 0,
};

const defaultTradingData = {
  power_trading: 0,
  power_trading_total: 0,
};

const defaultAssetsData = {
  power_assets: 0,
  power_assets_total: 0,
};

export const GlanceTab = ({ building, complex }) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const currencySymbol = user.currency.symbol;
  const unitSystem = user.unit_system;
  const { period, startDate, endDate } = useSelector((state) => state.selectedPeriods);
  const TOGGLE_OPTIONS = [`${currencySymbol}/Sqft`, "Total"];
  const [toggleValue, setToggleValue] = useState(TOGGLE_OPTIONS[0]);
  const [revenueData, setRevenueData] = useState(defaultRevenueData);
  const [vacancyData, setVacancyData] = useState(defaultVacancyData);
  const [carbonData, setCarbonData] = useState(defaultCarbonData);
  const [tenantsData, setTenantsData] = useState(defaultTenantsData);
  const [tradingData, setTradingData] = useState(defaultTradingData);
  const [assetsData, setAssetsData] = useState(defaultAssetsData);
  const [noiIncrease, setNoiIncrease] = useState(0);

  const url = building ? `praas/v1/buildings/${building.id}` : `praas/v2/complexes/${complex.id}`;

  useEffect(() => {
    const dateQueryParams = {
      period,
      startDate,
      endDate,
    };
    getData(`${url}/revenues/`, setRevenueData, dateQueryParams);
    getData(`${url}/vacancy/`, setVacancyData, dateQueryParams);
    getData(`${url}/carbon/`, setCarbonData, dateQueryParams);
    getData(`${url}/tenants/`, setTenantsData, dateQueryParams);
    getData(`${url}/trading/`, setTradingData, dateQueryParams);
    getData(`${url}/assets/`, setAssetsData, dateQueryParams);
    getData(`${url}/noi/`, (data) => setNoiIncrease(data.noi_increase), dateQueryParams);

    return () => {
      setRevenueData(defaultRevenueData);
      setVacancyData(defaultVacancyData);
      setCarbonData(defaultCarbonData);
      setTenantsData(defaultTenantsData);
      setTradingData(defaultTradingData);
      setAssetsData(defaultAssetsData);
      setNoiIncrease(0);
    };
  }, [period, startDate, endDate, building, complex]);

  const mapCenter = useMemo(() => {
    if (building) {
      return { lat: building.lat, lng: building.long };
    }

    if (complex?.buildings?.length) {
      let sumLat = 0;
      let sumLng = 0;

      complex.buildings.forEach((complexBuilding) => {
        sumLat += complexBuilding.lat;
        sumLng += complexBuilding.lng;
      });

      return {
        lat: sumLat / complex?.buildings?.length,
        lng: sumLng / complex?.buildings?.length,
      };
    }

    return null;
  }, [complex, building]);

  const getVacancyText = (vacancyData) => {
    if (vacancyData.physical_vacancy !== null && vacancyData.economic_vacancy !== null) {
      return `Physical ${vacancyData.physical_vacancy}%; Economic ${vacancyData.economic_vacancy}%`;
    } else if (vacancyData.physical_vacancy !== null) {
      return `Physical ${vacancyData.physical_vacancy}%`;
    }
    return `Economic ${vacancyData.economic_vacancy}%`;
  };

  const getImagePath = () => {
    if (building) {
      return building.image;
    }

    if (complex) {
      return complex.image;
    }

    return null;
  };

  const image = getImagePath();

  const buildings = building
    ? [
        {
          ...building,
          lng: building.long,
        },
      ]
    : complex.buildings;

  return (
    <Box className={classes.root}>
      <ActionTimeGroup
        hideIntegrateBtn
        buttonsGroupStyle={{ alignSelf: "flex-start" }}
        hideTimeRange
      />

      <Grid container spacing={2}>
        <Grid item xs={image ? 9 : 12}>
          <Box className={classes.locationBox}>
            <GoogleMapReact
              bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
              defaultCenter={DEFAULT_CENTER}
              center={mapCenter}
              defaultZoom={3}
            >
              {buildings?.map((building) => (
                <LocationIcon
                  key={building.id}
                  lat={building.lat}
                  lng={building.lng}
                  color={COLORS.blue}
                  style={{ position: "relative", top: -42, left: -16 }}
                  text={building.name}
                  textStyle={{
                    color: COLORS.blue,
                    background: COLORS.white,
                    borderRadius: 4,
                    padding: "0 10px",
                    fontWeight: 700,
                    position: "absolute",
                    top: -10,
                    right: 10,
                  }}
                />
              ))}
            </GoogleMapReact>
          </Box>
        </Grid>
        {image ? (
          <Grid item xs={3} container justifyContent="center" alignItems="center">
            <Box className={classes.locationBox} style={{ width: "100%" }}>
              <Icon root={classes.imageRoot} src={image} />
            </Box>
          </Grid>
        ) : null}
      </Grid>

      <Grid container alignItems="center" style={{ width: "fit-content" }}>
        <IconTitle iconSrc={InAGlanceBuildingIcon} />
        <Typography className={classes.title}>BUILDING KPIs</Typography>
        <Typography className={classes.separated}>-</Typography>
        {
          // Hide it for now, we need to fix this ticket
          // https://app.asana.com/0/1201502846243586/1203798949370109
          // <Typography className={classes.timeRangeText} color="primary">
          //   {showDates(period, startDate, endDate)}
          // </Typography>
        }
        <Toggle
          value={toggleValue}
          options={TOGGLE_OPTIONS}
          width="35px"
          onChanged={setToggleValue}
        />
      </Grid>

      <Grid container spacing={2}>
        {[
          {
            param: GLANCE_PARAMS.rental_revenue,
            rate: getRate(revenueData.comparison),
            title: "Rental Revenues",
            content:
              toggleValue === TOGGLE_OPTIONS[0]
                ? getConditionalText(
                    revenueData.rental_revenues,
                    `${currencyFormatter(
                      revenueData.rental_revenues,
                      currencySymbol
                    )}/${unitSystem}`
                  )
                : getConditionalText(
                    revenueData.rental_revenues_total,
                    currencyFormatter(revenueData.rental_revenues_total, currencySymbol)
                  ),
          },
          {
            param: GLANCE_PARAMS.vacancy,
            rate: getRate(vacancyData.comparison),
            title: "Vacancy",
            content: getConditionalText(
              vacancyData.physical_vacancy !== null || vacancyData.economic_vacancy !== null,
              getVacancyText(vacancyData)
            ),
          },
          {
            param: GLANCE_PARAMS.carbon_reduction,
            rate: getRate(carbonData.comparison),
            title: "Carbon Reduction",
            content:
              toggleValue === TOGGLE_OPTIONS[0]
                ? getConditionalText(
                    carbonData.carbon_reduction,
                    `${nCO2Formatter(carbonData.carbon_reduction)}/${unitSystem}`
                  )
                : getConditionalText(
                    carbonData.carbon_reduction_total,
                    nCO2Formatter(carbonData.carbon_reduction_total)
                  ),
          },
          {
            param: GLANCE_PARAMS.noi_increase,
            rate: noiIncrease > 0 ? GLANCE_PARAMS.good : GLANCE_PARAMS.bad,
            title: "NOI Increase",
            content: `${noiIncrease > 0 ? "+" : ""}${noiIncrease}%`,
          },
          {
            param: GLANCE_PARAMS.connected_tenant,
            rate: getRate(tenantsData.comparison),
            title: "Connected Tenants",
            content: getConditionalText(
              tenantsData.total_connected_tenants > 0,
              `${tenantsData.percent_connected_tenants}%`
            ),
            desc: getConditionalText(
              tenantsData.total_connected_tenants > 0,
              `(${tenantsData.connected_tenants_count}/total ${tenantsData.total_connected_tenants})`,
              ""
            ),
          },
          {
            param: GLANCE_PARAMS.power_trading_revenue,
            rate: getRate(tradingData.comparison),
            title: "Power Trading Rev.",
            content:
              toggleValue === TOGGLE_OPTIONS[0]
                ? `${currencyFormatter(tradingData.power_trading, currencySymbol)}/${unitSystem}`
                : currencyFormatter(tradingData.power_trading_total, currencySymbol),
          },
          {
            param: GLANCE_PARAMS.power_asset_revenue,
            rate: getRate(assetsData.comparison),
            title: "Power Assets Rev.",
            content:
              toggleValue === TOGGLE_OPTIONS[0]
                ? `${currencyFormatter(assetsData.power_assets, currencySymbol)}/${unitSystem}`
                : currencyFormatter(assetsData.power_assets_total, currencySymbol),
          },
          {
            param: GLANCE_PARAMS.building_profitability,
            title: "Building Profitability",
            content: building?.profitability || NO_DATA_TEXT,
          },
        ].map((entry, index) => (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={index}
            className={classes.rows}
          >
            <InfoCard
              param={entry.param}
              rate={entry.rate}
              title={entry.title}
              content={entry.content}
              desc={entry.desc}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
